import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, UserMinus } from "lucide-react";

interface RemoveFromTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRemove: (data: any) => Promise<void>;
  member: any;
  teamName: string;
}

export function RemoveFromTeamModal({
  isOpen,
  onClose,
  onRemove,
  member,
  teamName
}: RemoveFromTeamModalProps) {
  const [reason, setReason] = useState('');
  const [notifyMember, setNotifyMember] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    setIsSubmitting(true);
    try {
      await onRemove({
        reason: reason.trim(),
        notifyMember
      });
      onClose();
      setReason('');
      setNotifyMember(true);
    } catch (error) {
      console.error('Failed to remove member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setReason('');
      setNotifyMember(true);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="h-5 w-5" />
            Remove Team Member
          </DialogTitle>
          <DialogDescription>
            Remove {member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`} from {teamName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Member Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`}</p>
                  <p className="text-sm text-muted-foreground">{member.user?.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Role: {member.role || 'Member'}</p>
                  {member.user?.jobTitle && (
                    <p className="text-sm text-muted-foreground">{member.user.jobTitle}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Removal</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for removing this member from the team..."
                required
                rows={3}
              />
            </div>

            {/* Notification Option */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify"
                checked={notifyMember}
                onCheckedChange={(checked) => setNotifyMember(checked === true)}
              />
              <Label htmlFor="notify" className="text-sm">
                Notify member via email about their removal
              </Label>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Warning</p>
                <p className="text-red-700">
                  This action will permanently remove{' '}
                  <strong>{member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`}</strong>{' '}
                  from <strong>{teamName}</strong>. They will lose access to all team resources
                  and projects. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!reason.trim() || isSubmitting}
            >
              {isSubmitting ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
